import { defineStore } from 'pinia';

/**
 * In-flight promise for the current session load.
 * Lets components (e.g. the dashboard) await session hydration before reading
 * role/module state, avoiding a race on first magic-link load.
 */
let sessionPromise = null;

/**
 * Portal store - manages portal authentication state
 * Used by customer/supplier portal views
 */
export const usePortalStore = defineStore('portal', {
    state: () => ({
        token: '',
        contact: null,
        customer: null,
        supplier: null,
        // A prospect's portal: their quotes, what they sign, nothing of a client's.
        prospect: null,
        portalType: null,
        organization: null,
        orgSlug: '',
        expiresAt: null,
        enabledModules: [],
        cartItemCount: 0,
        // A session held to one document — the link of a document sent to a
        // contact whose portal is closed: { path: 'invoices', id: 12 }, or null.
        document: null
    }),

    getters: {
        isAuthenticated: state => {
            if (!state.token || !state.expiresAt) return false;
            return new Date(state.expiresAt) > new Date();
        },

        apiBase: state => {
            return `/api/portal/${state.orgSlug}`;
        },

        displayName: state => {
            if (!state.contact) return '';
            const parts = [];
            if (state.contact.firstname) parts.push(state.contact.firstname);
            if (state.contact.lastname) parts.push(state.contact.lastname);
            return parts.join(' ') || state.contact.company || '';
        },

        // A role is the contact's, as a client's and a supplier's are, and one
        // person can hold several: a prospect sees their quotes beside the rest.
        // A session opened as a prospect (the link of a quote) shows only that.
        isProspect: state =>
            state.portalType === 'prospect' || (!!state.contact?.is_prospect && !state.contact?.is_customer),
        isCustomer: state => state.portalType !== 'prospect' && !!state.contact?.is_customer,
        isSupplier: state => state.portalType !== 'prospect' && !!state.contact?.is_supplier,

        /**
         * Only a prospect — no client or supplier role beside it. Their portal is
         * what is addressed to them: quotes, contracts, documents, their profile.
         */
        isProspectOnly() {
            return this.isProspect && !this.isCustomer && !this.isSupplier;
        },

        isDocumentOnly: state => !!state.document,

        isModuleEnabled: state => slug => {
            if (!state.enabledModules.length) return true;
            return state.enabledModules.includes(slug);
        },

        logo: state => state.organization?.logo || null,

        document_logo: state => state.organization?.document_logo || null,

        orgAddress: state => {
            if (!state.organization) return '';
            const org = state.organization;
            const parts = [];
            if (org.name) parts.push(org.name);
            if (org.address1) parts.push(org.address1);
            if (org.city) {
                let line = org.city;
                if (org.state) line += ', ' + org.state;
                if (org.postal_code) line += ' ' + org.postal_code;
                parts.push(line);
            }
            const website = org.websites?.[0]?.value || null;
            if (website) parts.push(website);
            const phone = org.phones?.[0]?.value || null;
            if (phone) parts.push(phone);
            return parts.join('\n');
        }
    },

    actions: {
        /**
         * Initialize from localStorage
         */
        init(orgSlug) {
            this.orgSlug = orgSlug;

            const stored = localStorage.getItem(`portal_${orgSlug}`);
            if (stored) {
                try {
                    const data = JSON.parse(stored);
                    this.token = data.token || '';
                    this.contact = data.contact || null;
                    this.customer = data.customer || null;
                    this.supplier = data.supplier || null;
                    this.prospect = data.prospect || null;
                    this.portalType = data.portalType || null;
                    this.organization = data.organization || null;
                    this.expiresAt = data.expiresAt || null;
                    this.enabledModules = data.enabledModules || [];
                    this.cartItemCount = data.cartItemCount || 0;
                    this.document = data.document || null;
                } catch (e) {
                    this.clear();
                }
            }
        },

        /**
         * Set auth data from verify response
         */
        setAuth(data) {
            this.token = data.token;
            this.contact = data.contact;
            this.expiresAt = data.expires_at;

            this.persist();
        },

        /**
         * Set session info from /me endpoint
         */
        setSession(data) {
            this.contact = data.contact;
            this.customer = data.customer || null;
            this.supplier = data.supplier || null;
            this.prospect = data.prospect || null;
            this.portalType = data.portal_type || null;
            this.organization = data.organization || null;
            this.enabledModules = data.enabled_modules || [];
            this.document = data.document || null;

            this.persist();
        },

        /**
         * Persist to localStorage
         */
        persist() {
            localStorage.setItem(
                `portal_${this.orgSlug}`,
                JSON.stringify({
                    token: this.token,
                    contact: this.contact,
                    customer: this.customer,
                    supplier: this.supplier,
                    prospect: this.prospect,
                    portalType: this.portalType,
                    organization: this.organization,
                    expiresAt: this.expiresAt,
                    enabledModules: this.enabledModules,
                    cartItemCount: this.cartItemCount,
                    document: this.document
                })
            );
        },

        /**
         * Make authenticated API request
         */
        async api(method, url, data = null) {
            const config = {
                method,
                url: `${this.apiBase}${url}`,
                headers: {
                    Authorization: `Bearer ${this.token}`
                }
            };
            if (data) {
                if (method.toLowerCase() === 'get') {
                    config.params = data;
                } else {
                    config.data = data;
                }
            }
            return axios(config);
        },

        /**
         * Load session info. Tracks the in-flight promise so callers can await
         * completion via whenSessionReady().
         */
        loadSession() {
            sessionPromise = this.fetchSession();
            return sessionPromise;
        },

        async fetchSession() {
            try {
                const response = await this.api('get', '/auth/me');
                this.setSession(response.data);

                if (this.isCustomer && !this.isDocumentOnly && this.isModuleEnabled('store')) {
                    this.loadCartItemCount();
                }
            } catch (error) {
                if (error.response?.status === 401) {
                    this.clear();
                }
                throw error;
            }
        },

        /**
         * Resolve once the current session load has settled (never rejects).
         * Resolves immediately if no session load is in flight.
         */
        whenSessionReady() {
            return (sessionPromise || Promise.resolve()).catch(() => {});
        },

        async loadCartItemCount() {
            try {
                const response = await this.api('get', '/store/cart/count');
                this.cartItemCount = response.data.count || 0;
                this.persist();
            } catch {
                // silently fail
            }
        },

        /**
         * Clear all portal data
         */
        clear() {
            const slug = this.orgSlug;
            sessionPromise = null;
            this.token = '';
            this.contact = null;
            this.customer = null;
            this.supplier = null;
            this.organization = null;
            this.expiresAt = null;
            this.enabledModules = [];
            this.cartItemCount = 0;
            this.document = null;
            localStorage.removeItem(`portal_${slug}`);
        }
    }
});
